import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { VERFUEGUNGS_ARTEN } from "../../../data/verfuegungsarten";
import { FieldRow } from "../formPrimitives";
import { MehrfachSelect } from "./MehrfachSelect";
import { PersonSuchfeldMitNeu } from "./PersonSuchfeldMitNeu";
import type { ContactRef } from "../../relationsInline/types";
import { refDisplayName } from "../../relationsInline/types";
import { apiUrl } from "../../../utils/api";
import { elementMitId } from "../../windowManager/browserFenster/fensterRegister";

export interface VerfuegungStateItem {
  art: string; // code aus VERFUEGUNGS_ARTEN
  aktiv: boolean;
  bevollmaechtigterId?: number;
  bevollmaechtigterName?: string;
  bevollmaechtigterRef?: ContactRef | null;
  fileUrl?: string;
  fileName?: string;
  fileRef?: File;
}

export interface VertretungStateItem {
  id: string;
  rollen: string[]; // e.g. ["Vorsorgevollmacht", "Generalvollmacht"]
  rolle?: string; // backwards compatibility
  zielContactId?: number;
  zielName: string;
  zielRef?: ContactRef | null;
  fileUrl?: string;
  fileName?: string;
  fileRef?: File;
}

export const VOLLMACHTEN_OPTIONEN: { id: string; label: string }[] = [
  { id: "Vorsorgevollmacht", label: "Vorsorgevollmacht" },
  { id: "Generalvollmacht", label: "Generalvollmacht" },
  { id: "Bankvollmacht", label: "Bank-, Konto- und Depotvollmacht" },
  { id: "Patientenverfuegung", label: "Patientenverfügung" },
  { id: "Betreuung", label: "Betreuung" },
  { id: "Vormundschaft", label: "Vormundschaft" },
  { id: "Pflegschaft", label: "Pflegschaft" },
  { id: "Testamentsvollstreckung", label: "Testamentsvollstreckung" },
  { id: "Sorgerecht", label: "Sorgerechtsverfügung" },
  { id: "Trauerverfuegung", label: "Trauerverfügung" },
  { id: "Tierverfuegung", label: "Haus- und Großtierverfügung" },
];

const UPLOAD_ICON = (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M14 10v2.667A1.333 1.333 0 0 1 12.667 14H3.333A1.333 1.333 0 0 1 2 12.667V10" />
    <path d="M4.667 5.333 8 2l3.333 3.333" />
    <path d="M8 2v8" />
  </svg>
);

const DOKUMENT_ICON = (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="M9.333 2H4a1.333 1.333 0 0 0-1.333 1.333v9.334A1.333 1.333 0 0 0 4 14h8a1.333 1.333 0 0 0 1.333-1.333V6.667L9.333 2Z" />
    <path d="M9.333 2v4.667H14" />
  </svg>
);

const ENTFERNEN_ICON = (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 0 1 1.334-1.334h2.666a1.333 1.333 0 0 1 1.334 1.334V4M6.667 7.333v4.667M9.333 7.333v4.667M12.667 4v9.333a1.333 1.333 0 0 1-1.334 1.334H4.667A1.333 1.333 0 0 1 3.333 13.333V4" />
  </svg>
);

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    rowGap: "28px",
  },
  sectionBlock: {
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  sectionTitle: {
    fontFamily: REFINED.fontUI,
    fontSize: "1.15rem",
    fontWeight: 800,
    color: REFINED.ink,
    margin: 0,
    borderBottom: `2px solid ${REFINED.goldLine}`,
    paddingBottom: "8px",
  },
  cardItem: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    padding: "16px",
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("12px"),
  },
  cardItemActive: {
    ...shorthands.borderColor(REFINED.aub),
    boxShadow: REFINED.cardShadow,
  },
  itemTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "10px",
  },
  titleGroup: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
  },
  iconBox: {
    width: "28px",
    height: "28px",
    ...shorthands.borderRadius("6px"),
    backgroundColor: REFINED.aub50,
    color: REFINED.aub,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  itemTitle: {
    fontFamily: REFINED.fontUI,
    fontSize: "1.05rem",
    fontWeight: 700,
    color: REFINED.ink,
  },
  itemHinweis: {
    fontFamily: REFINED.fontUI,
    fontSize: "0.82rem",
    color: REFINED.ink3,
    marginTop: "2px",
  },
  radioGroup: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "16px",
    fontFamily: REFINED.fontUI,
    fontSize: "0.92rem",
    color: REFINED.ink,
  },
  radioLabel: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    cursor: "pointer",
    "& input[type='radio']": {
      accentColor: REFINED.aub,
      cursor: "pointer",
    },
  },
  personSlot: {
    marginTop: "10px",
    paddingTop: "14px",
    borderTop: `1px solid ${REFINED.line}`,
    display: "flex",
    flexDirection: "column",
    rowGap: "14px",
  },
  fieldLabel: {
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.875rem",
    color: REFINED.ink,
  },
  divider: {
    height: "1px",
    backgroundColor: REFINED.line,
    ...shorthands.borderWidth("0"),
    ...shorthands.margin("16px", "0"),
  },
  removeBtn: {
    ...shorthands.border("1px", "solid", REFINED.err),
    ...shorthands.borderRadius("6px"),
    backgroundColor: REFINED.white,
    color: REFINED.err,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.8rem",
    padding: "4px 10px",
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.errBg },
  },
  addToggleBtn: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.88rem",
    padding: "8px 18px",
    cursor: "pointer",
    alignSelf: "flex-end",
    marginTop: "8px",
    transitionProperty: "background-color",
    transitionDuration: "140ms",
    ":hover": { backgroundColor: REFINED.aub50 },
  },
  optionPillActive: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: REFINED.aub,
    color: REFINED.white,
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("999px"),
    padding: "6px 14px",
    fontSize: "0.82rem",
    fontWeight: 700,
    fontFamily: REFINED.fontUI,
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(125,32,105,0.22)",
    transitionProperty: "all",
    transitionDuration: "120ms",
  },
  optionPillInactive: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: REFINED.white,
    color: REFINED.ink,
    ...shorthands.border("1.2px", "solid", REFINED.line),
    ...shorthands.borderRadius("999px"),
    padding: "6px 14px",
    fontSize: "0.82rem",
    fontWeight: 600,
    fontFamily: REFINED.fontUI,
    cursor: "pointer",
    transitionProperty: "all",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: REFINED.aub50,
      ...shorthands.borderColor(REFINED.aub),
    },
  },
  fileActionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    ...shorthands.border("1px", "solid", REFINED.aub),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontSize: "0.82rem",
    fontWeight: 600,
    fontFamily: REFINED.fontUI,
    padding: "6px 12px",
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.aub50 },
  },
  fileDeleteBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.border("1px", "solid", REFINED.err),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    color: REFINED.err,
    padding: "6px 9px",
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.errBg },
  },
});

const ArtIcon: React.FC<{ code: string }> = ({ code }) => {
  if (code.includes("BANK")) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.7}>
        <path d="M2 5l6-3 6 3M3 6v6M7 6v6M9 6v6M13 6v6M2 13h12" />
      </svg>
    );
  }
  if (code.includes("PATIENT")) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.7}>
        <path d="M8 3v10M3 8h10" />
      </svg>
    );
  }
  if (code.includes("TESTAMENT")) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.7}>
        <path d="M3 2h7l4 4v8H3V2zM10 2v4h4" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.7}>
      <path d="M8 2l5 2.5v4c0 3.2-2.1 6-5 6.8-2.9-.8-5-3.6-5-6.8v-4L8 2z" />
    </svg>
  );
};

const FullWidthUpload: React.FC<{
  id: string;
  fileUrl?: string;
  fileName?: string;
  uploading?: boolean;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove: () => void;
  onOpen?: (url: string) => void;
}> = ({ id, fileUrl, fileName, uploading, onUpload, onRemove, onOpen }) => {
  const s = useStyles();
  const inputId = `doc-upload-${id}`;
  return (
    <div style={{ width: "100%", marginTop: "4px" }}>
      <input
        id={inputId}
        type="file"
        accept="*/*"
        style={{ display: "none" }}
        onChange={onUpload}
      />
      {fileUrl || fileName ? (
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 14px",
            backgroundColor: REFINED.aub50,
            border: `1.5px solid ${REFINED.aub}`,
            borderRadius: "10px",
            boxSizing: "border-box",
            gap: "10px",
          }}
        >
          <button
            type="button"
            onClick={() => onOpen?.(fileUrl || "")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "none",
              border: "none",
              padding: 0,
              color: REFINED.aub,
              fontWeight: 700,
              fontSize: "0.88rem",
              fontFamily: REFINED.fontUI,
              cursor: "pointer",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={`Dokument öffnen: ${fileName || "Dokument"}`}
          >
            {DOKUMENT_ICON}
            <span style={{ textDecoration: "underline" }}>{fileName || "Dokument"}</span>
          </button>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0 }}>
            <button
              type="button"
              className={s.fileActionBtn}
              disabled={uploading}
              onClick={() => elementMitId(inputId)?.click()}
            >
              {UPLOAD_ICON} Ersetzen
            </button>
            <button
              type="button"
              className={s.fileDeleteBtn}
              onClick={onRemove}
              title="Dokument entfernen"
              aria-label="Dokument entfernen"
            >
              {ENTFERNEN_ICON}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={uploading}
          onClick={() => elementMitId(inputId)?.click()}
          style={{
            width: "100%",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            minHeight: "42px",
            padding: "10px 16px",
            backgroundColor: "#faf8f5",
            border: "1.5px dashed #d4c29d",
            borderRadius: "10px",
            color: REFINED.aub,
            fontFamily: REFINED.fontUI,
            fontSize: "0.88rem",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 140ms ease",
          }}
        >
          {UPLOAD_ICON}
          <span>{uploading ? "Lädt hoch…" : "Dokument hochladen"}</span>
        </button>
      )}
    </div>
  );
};

export const getVertretungBeschriftung = (rollen?: string[]): string => {
  if (!rollen || rollen.length === 0) return "Bitte wählen";
  const labels = rollen.map((r) => {
    const opt = VOLLMACHTEN_OPTIONEN.find((o) => o.id === r || o.label === r);
    return opt?.label || r;
  });
  return labels.join(", ");
};

export const VertretungVollmachtenAbschnitt: React.FC<{
  verfuegungen: VerfuegungStateItem[];
  onVerfuegungenChange: (list: VerfuegungStateItem[]) => void;
  vertretungen: VertretungStateItem[];
  onVertretungenChange: (list: VertretungStateItem[]) => void;
  uploadFile?: (file: File) => Promise<{ url: string; name?: string }>;
  onOpenDocument?: (url: string) => void;
}> = ({
  verfuegungen,
  onVerfuegungenChange,
  vertretungen,
  onVertretungenChange,
  uploadFile,
  onOpenDocument,
}) => {
  const s = useStyles();
  const [uploadingId, setUploadingId] = React.useState<string | null>(null);

  const getVerfuegung = (art: string): VerfuegungStateItem => {
    return (
      verfuegungen.find((v) => v.art === art) || {
        art,
        aktiv: false,
      }
    );
  };

  const setVerfuegung = (art: string, patch: Partial<VerfuegungStateItem>) => {
    const existing = verfuegungen.find((v) => v.art === art);
    if (existing) {
      onVerfuegungenChange(
        verfuegungen.map((v) => (v.art === art ? { ...v, ...patch } : v)),
      );
    } else {
      onVerfuegungenChange([...verfuegungen, { art, aktiv: false, ...patch }]);
    }
  };

  const updateVertretung = (id: string, patch: Partial<VertretungStateItem>) => {
    onVertretungenChange(
      vertretungen.map((it) => (it.id === id ? { ...it, ...patch } : it)),
    );
  };

  const removeVertretung = (id: string) => {
    onVertretungenChange(vertretungen.filter((it) => it.id !== id));
  };

  const addVertretung = () => {
    const newId = "vert-" + Date.now() + "-" + Math.random().toString(36).substring(2, 6);
    onVertretungenChange([
      ...vertretungen,
      {
        id: newId,
        rollen: ["Vorsorgevollmacht"],
        rolle: "Vorsorgebevollmächtigter",
        zielName: "",
        zielRef: null,
      },
    ]);
  };

  const toggleRolle = (id: string, optionId: string) => {
    const item = vertretungen.find((it) => it.id === id);
    if (!item) return;
    const current = item.rollen || (item.rolle ? [item.rolle] : []);
    const next = current.includes(optionId)
      ? current.filter((r) => r !== optionId)
      : [...current, optionId];
    updateVertretung(id, {
      rollen: next,
      rolle: next[0] || "",
    });
  };

  const handleFileUpload = async (
    targetId: string,
    e: React.ChangeEvent<HTMLInputElement>,
    type: "verfuegung" | "vertretung",
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setUploadingId(targetId);
    try {
      let fileUrl = "";
      let fileName = file.name;

      if (uploadFile) {
        const res = await uploadFile(file);
        fileUrl = res.url;
        if (res.name) fileName = res.name;
      } else {
        try {
          const fd = new FormData();
          fd.append("file", file);
          const res = await fetch(apiUrl("upload/contact-authority"), {
            method: "POST",
            body: fd,
          });
          if (res.ok) {
            const data = await res.json();
            fileUrl = data.url || data.filePath || "";
            if (data.name) fileName = data.name;
          }
        } catch {
          // Fallback to blob URL in dev/offline
        }
        if (!fileUrl) {
          fileUrl = URL.createObjectURL(file);
        }
      }

      if (type === "verfuegung") {
        setVerfuegung(targetId, {
          fileUrl,
          fileName,
          fileRef: file,
        });
      } else {
        updateVertretung(targetId, {
          fileUrl,
          fileName,
          fileRef: file,
        });
      }
    } catch (err) {
      console.error("Upload-Fehler:", err);
      // Fallback: blob URL
      const fileUrl = URL.createObjectURL(file);
      if (type === "verfuegung") {
        setVerfuegung(targetId, { fileUrl, fileName: file.name, fileRef: file });
      } else {
        updateVertretung(targetId, { fileUrl, fileName: file.name, fileRef: file });
      }
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div className={s.container}>
      {/* ── 1. Vollmachten & Verfügungen des Kontaktes ── */}
      <div className={s.sectionBlock}>
        <h3 className={s.sectionTitle}>Vollmachten &amp; Verfügungen des Kontaktes</h3>
        {VERFUEGUNGS_ARTEN.map((va) => {
          const item = getVerfuegung(va.code);
          return (
            <div
              key={va.code}
              className={mergeClasses(s.cardItem, item.aktiv && s.cardItemActive)}
            >
              <div className={s.itemTop}>
                <div className={s.titleGroup}>
                  <div className={s.iconBox}>
                    <ArtIcon code={va.code} />
                  </div>
                  <div>
                    <div className={s.itemTitle}>{va.label}</div>
                    <div className={s.itemHinweis}>{va.hinweis}</div>
                  </div>
                </div>

                <div className={s.radioGroup}>
                  <label className={s.radioLabel}>
                    <input
                      type="radio"
                      name={`va-${va.code}`}
                      checked={item.aktiv === true}
                      onChange={() => setVerfuegung(va.code, { aktiv: true })}
                    />
                    <span>Ja</span>
                  </label>
                  <label className={s.radioLabel}>
                    <input
                      type="radio"
                      name={`va-${va.code}`}
                      checked={item.aktiv === false}
                      onChange={() =>
                        setVerfuegung(va.code, {
                          aktiv: false,
                          bevollmaechtigterId: undefined,
                          bevollmaechtigterName: undefined,
                          bevollmaechtigterRef: undefined,
                          fileUrl: undefined,
                          fileName: undefined,
                          fileRef: undefined,
                        })
                      }
                    />
                    <span>Nein</span>
                  </label>
                </div>
              </div>

              {item.aktiv && (
                <div className={s.personSlot}>
                  <PersonSuchfeldMitNeu
                    label="Bevollmächtigte Person auswählen oder neu erfassen"
                    name={`bev-${va.code}`}
                    placeholder="Person im System suchen …"
                    value={
                      item.bevollmaechtigterRef ||
                      (item.bevollmaechtigterId || item.bevollmaechtigterName
                        ? {
                            contactId: item.bevollmaechtigterId,
                            displayName: item.bevollmaechtigterName,
                            lastName: item.bevollmaechtigterName,
                            type: "Person",
                          }
                        : null)
                    }
                    onChange={(ref) => {
                      if (ref) {
                        setVerfuegung(va.code, {
                          bevollmaechtigterId: ref.contactId,
                          bevollmaechtigterName: refDisplayName(ref),
                          bevollmaechtigterRef: ref,
                        });
                      } else {
                        setVerfuegung(va.code, {
                          bevollmaechtigterId: undefined,
                          bevollmaechtigterName: undefined,
                          bevollmaechtigterRef: undefined,
                        });
                      }
                    }}
                  />

                  <div>
                    <span className={s.fieldLabel}>Dokument / Vollmacht</span>
                    <FullWidthUpload
                      id={`va-${va.code}`}
                      fileUrl={item.fileUrl}
                      fileName={item.fileName}
                      uploading={uploadingId === va.code}
                      onUpload={(e) => handleFileUpload(va.code, e, "verfuegung")}
                      onRemove={() =>
                        setVerfuegung(va.code, {
                          fileUrl: "",
                          fileName: "",
                          fileRef: undefined,
                        })
                      }
                      onOpen={onOpenDocument}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── 2. Dieser Kontakt vertritt andere Personen ── */}
      <div className={s.sectionBlock}>
        <h3 className={s.sectionTitle}>Dieser Kontakt vertritt andere Personen</h3>

        {vertretungen.length === 0 && (
          <div style={{ color: REFINED.ink3, fontSize: "0.88rem", padding: "4px 0" }}>
            Keine Vertretungsrollen für andere Personen erfasst.
          </div>
        )}

        {vertretungen.map((item, idx) => (
          <div
            key={item.id}
            style={{
              display: "flex",
              flexDirection: "column",
              rowGap: "14px",
              position: "relative",
            }}
          >
            {idx > 0 && <hr className={s.divider} />}

            {/* Zwei Dropdowns wie vorher: 1. Vertretungsfunktion (Multiselect), 2. Vertretene Person */}
            <FieldRow columns={2}>
              <MehrfachSelect
                label="Vertretungsfunktion"
                name={`vert-rolle-${item.id}`}
                value={item.rollen || []}
                options={VOLLMACHTEN_OPTIONEN.map((opt) => ({
                  value: opt.id,
                  label: opt.label,
                }))}
                onToggle={(val) => toggleRolle(item.id, val)}
                beschriftung={getVertretungBeschriftung(item.rollen)}
                emptyText="Keine Vollmacht gefunden"
              />

              <PersonSuchfeldMitNeu
                label="Vertretene Person suchen"
                name={`vert-person-${item.id}`}
                placeholder="Person suchen …"
                value={
                  item.zielRef ||
                  (item.zielContactId || item.zielName
                    ? {
                        contactId: item.zielContactId,
                        displayName: item.zielName,
                        lastName: item.zielName,
                        type: "Person",
                      }
                    : null)
                }
                onChange={(ref) => {
                  if (ref) {
                    updateVertretung(item.id, {
                      zielContactId: ref.contactId,
                      zielName: refDisplayName(ref),
                      zielRef: ref,
                    });
                  } else {
                    updateVertretung(item.id, {
                      zielContactId: undefined,
                      zielName: "",
                      zielRef: null,
                    });
                  }
                }}
              />
            </FieldRow>

            {/* Dokument hochladen (volle Breite) */}
            <div>
              <span className={s.fieldLabel}>Dokument / Vollmacht</span>
              <FullWidthUpload
                id={`vert-${item.id}`}
                fileUrl={item.fileUrl}
                fileName={item.fileName}
                uploading={uploadingId === item.id}
                onUpload={(e) => handleFileUpload(item.id, e, "vertretung")}
                onRemove={() =>
                  updateVertretung(item.id, {
                    fileUrl: "",
                    fileName: "",
                    fileRef: undefined,
                  })
                }
                onOpen={onOpenDocument}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <button
                type="button"
                className={s.removeBtn}
                onClick={() => removeVertretung(item.id)}
              >
                Entfernen
              </button>
            </div>
          </div>
        ))}

        <button
          type="button"
          className={s.addToggleBtn}
          style={{ alignSelf: "flex-end" }}
          onClick={addVertretung}
        >
          + Vertretungsrolle hinzufügen
        </button>
      </div>
    </div>
  );
};
